import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBrands, addBrand, updateBrand, deleteBrand, uploadBrandLogo } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

const PAGE_SIZE = 20;

const ManageBrands = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    brandName: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(brands.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, brands.length);
  const paginatedBrands = brands.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    setLoading(true);
    const result = await getBrands();
    if (result.success) {
      setBrands(result.brands);
    } else {
      setError('Failed to load brands: ' + result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setFormData({ brandName: '' });
    setLogoFile(null);
    setLogoPreview('');
    setShowModal(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ brandName: brand.brandName || '' });
    setLogoFile(null);
    setLogoPreview(brand.brandLogo || '');
    setShowModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      setLoading(true);
      const result = await deleteBrand(id);
      if (result.success) {
        await loadBrands();
        alert('Brand deleted successfully!');
      } else {
        alert('Failed to delete brand: ' + result.error);
      }
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.brandName.trim()) {
      alert('Please enter a brand name');
      return;
    }

    if (!logoFile && !logoPreview) {
      alert('Please upload a brand logo');
      return;
    }

    setSubmitting(true);

    try {
      let logoUrl = logoPreview;

      if (logoFile) {
        const brandId = editingBrand?.id || null;
        logoUrl = await uploadBrandLogo(logoFile, brandId);
      }

      if (!logoUrl) {
        alert('Please upload a brand logo');
        setSubmitting(false);
        return;
      }

      const brandData = {
        brandName: formData.brandName.trim(),
        brandLogo: logoUrl
      };

      if (editingBrand) {
        const result = await updateBrand(editingBrand.id, brandData);
        if (result.success) {
          await loadBrands();
          setShowModal(false);
          alert('Brand updated successfully!');
        } else {
          alert('Failed to update brand: ' + result.error);
        }
      } else {
        const result = await addBrand(brandData);
        if (result.success) {
          await loadBrands();
          setShowModal(false);
          setFormData({ brandName: '' });
          setLogoFile(null);
          setLogoPreview('');
          alert('Brand added successfully!');
        } else {
          alert('Failed to add brand: ' + result.error);
        }
      }
    } catch (err) {
      console.error('Error saving brand:', err);
      alert('Failed to save brand');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBrand(null);
    setFormData({ brandName: '' });
    setLogoFile(null);
    setLogoPreview('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="brands" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b-2 border-gray-200 pb-4">
            <div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors mb-2 text-sm"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px]" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Brands</h1>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 bg-gray-200 text-primary py-2 px-4 rounded font-bold hover:opacity-90 transition-opacity self-start md:self-auto shadow-md hover:shadow-none hover:translate-y-px disabled:opacity-60"
              disabled={loading}
            >
              <AddCircleOutlineIcon className="!w-5 !h-5" />
              Add Brand
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded shadow-sm overflow-x-auto">
            {loading && (
              <div className="p-8 text-center">
                <div className="text-gray-500">Loading brands...</div>
              </div>
            )}
            {!loading && brands.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-gray-500">No brands found. Add your first brand!</div>
              </div>
            )}
            {!loading && brands.length > 0 && (
              <>
                <table className="w-full text-left border-collapse min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-200">
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Logo</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Brand Name</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-900">
                    {paginatedBrands.map((brand, index) => (
                      <tr
                        key={brand.id}
                        className={`border-b border-gray-200 transition-colors hover:bg-gray-100 ${
                          index % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : ''
                        }`}
                      >
                        <td className="py-2 px-4">
                          {brand.brandLogo ? (
                            <img
                              src={brand.brandLogo}
                              alt={brand.brandName}
                              className="w-16 h-16 object-contain border border-gray-200 rounded bg-white"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-500 rounded">
                              No Logo
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-4 max-w-md">
                          <p className="font-medium truncate">{brand.brandName}</p>
                        </td>
                        <td className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEdit(brand)}
                              className="text-gray-400 hover:text-primary transition-colors p-1"
                              title="Edit"
                              disabled={loading}
                            >
                              <EditIcon className="!w-5 !h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(brand.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              title="Delete"
                              disabled={loading}
                            >
                              <DeleteIcon className="!w-5 !h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="bg-white border-t border-gray-200 p-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500 pl-2">
                    Showing {startIndex + 1} to {endIndex} of {brands.length} entries
                  </span>
                  <div className="flex items-center gap-1 pr-2">
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      type="button"
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon className="!w-5 !h-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        className={`w-8 h-8 rounded text-sm font-semibold flex items-center justify-center ${
                          page === currentPage
                            ? 'bg-primary text-white'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        onClick={() => setCurrentPage(page)}
                        type="button"
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      type="button"
                      aria-label="Next page"
                    >
                      <ChevronRightIcon className="!w-5 !h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleCloseModal}>
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl w-full mx-4 shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-3">
              <h2 className="text-xl font-bold text-primary">
                {editingBrand ? 'Edit Brand' : 'Add Brand'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  required
                  className="border border-gray-200 rounded p-2 bg-white text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm w-full"
                  placeholder="Enter brand name"
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Brand Logo <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  required={!logoPreview}
                  className="border border-gray-200 rounded p-2 bg-white text-sm w-full"
                  disabled={submitting}
                />
                {logoPreview && (
                  <div className="mt-2">
                    <img
                      src={logoPreview}
                      alt="Preview"
                      className="w-24 h-24 object-contain border border-gray-200 rounded bg-white"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  className="px-4 py-2 border-2 border-primary text-primary text-sm font-bold rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-blue-900 transition-colors shadow-sm disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingBrand ? 'Update Brand' : 'Add Brand'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageBrands;
